/**
 * The account API, as the pages expect to call it — with nothing behind it yet.
 *
 * Every page is written against this interface rather than against `fetch`, so
 * connecting the real backend is a change to this file alone. Until then each
 * call resolves after a short pause, which is enough to see the pages' loading
 * states, and signing in with Google or LinkedIn reports that it isn't wired up.
 *
 * What the real implementation has to guarantee, because the pages rely on it:
 *
 *   - `requestPasswordReset` resolves the same way whether or not the address
 *     has an account. Otherwise the page is a way to find out who has signed up.
 *   - `verifyEmail` is safe to call twice with the same token. React runs
 *     effects twice in development, and people double-click links in email.
 *   - `signUp` and `logIn` merge what this device already holds — the schedule,
 *     Save for Later, personal events, attendance and travel choices — into the
 *     account. Most people will arrive with half a week already built on the
 *     device they happen to be holding, and the whole point of an account is
 *     that the week follows them to the next one.
 */

export type OAuthProvider = 'google' | 'linkedin';

export type AuthResult = { ok: true } | { ok: false; message: string };

export const PROVIDER_NAME: Record<OAuthProvider, string> = {
  google: 'Google',
  linkedin: 'LinkedIn',
};

const pause = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Marks a call in the console while there is no server to receive it.
 * Addresses only — a password is never written anywhere, including here.
 */
function preview(action: string, email?: string): void {
  if (import.meta.env.DEV) console.info(`[account preview] ${action}`, email ?? '');
}

async function succeed(action: string, email?: string): Promise<AuthResult> {
  preview(action, email);
  await pause(700);
  return { ok: true };
}

export const authClient = {
  signUp(email: string, password: string): Promise<AuthResult> {
    void password;
    return succeed('sign up', email);
  },

  logIn(email: string, password: string): Promise<AuthResult> {
    void password;
    return succeed('log in', email);
  },

  resendVerification(email: string): Promise<AuthResult> {
    return succeed('resend verification', email);
  },

  verifyEmail(token: string): Promise<AuthResult> {
    void token;
    return succeed('verify email');
  },

  requestPasswordReset(email: string): Promise<AuthResult> {
    return succeed('request password reset', email);
  },

  resetPassword(token: string, password: string): Promise<AuthResult> {
    void token;
    void password;
    return succeed('reset password');
  },

  async startOAuth(provider: OAuthProvider): Promise<AuthResult> {
    preview(`continue with ${PROVIDER_NAME[provider]}`);
    await pause(400);
    return { ok: false, message: `${PROVIDER_NAME[provider]} sign-in isn't connected yet.` };
  },
};
